import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Edit2, X, Phone, Mail, User } from 'lucide-react';
import api from '../../services/api';

function FornecedorModal({ fornecedor, onClose }) {
  const qc = useQueryClient();
  const isEdit = !!fornecedor?.id;
  const [form, setForm] = useState({
    razao_social: fornecedor?.razao_social || '',
    nome_fantasia: fornecedor?.nome_fantasia || '',
    cnpj: fornecedor?.cnpj || '',
    email: fornecedor?.email || '',
    telefone: fornecedor?.telefone || '',
    contato_nome: fornecedor?.contato_nome || '',
    endereco: fornecedor?.endereco || '',
  });
  const [erro, setErro] = useState('');

  const save = useMutation({
    mutationFn: () => isEdit ? api.put(`/insumos/fornecedores/${fornecedor.id}`, form) : api.post('/insumos/fornecedores', form),
    onSuccess: () => { qc.invalidateQueries(['insumos-forn']); onClose(); },
    onError: e => setErro(e.response?.data?.error || 'Erro ao salvar'),
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-semibold text-gray-800 text-lg">{isEdit ? 'Editar Fornecedor' : 'Novo Fornecedor'}</h3>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>
        {erro && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{erro}</div>}

        <div className="space-y-3">
          {[
            { label: 'Razão Social *', key: 'razao_social' },
            { label: 'Nome Fantasia', key: 'nome_fantasia' },
            { label: 'CNPJ', key: 'cnpj', placeholder: '00.000.000/0000-00' },
            { label: 'E-mail', key: 'email', type: 'email' },
            { label: 'Telefone', key: 'telefone' },
            { label: 'Contato (nome)', key: 'contato_nome' },
            { label: 'Endereço', key: 'endereco' },
          ].map(({ label, key, type = 'text', placeholder }) => (
            <div key={key}>
              <label className="block text-xs text-gray-500 mb-1">{label}</label>
              <input type={type} value={form[key]} onChange={e => set(key, e.target.value)} placeholder={placeholder}
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || !form.razao_social}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {save.isPending ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InsumosFornecedores() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(null);
  const [busca, setBusca] = useState('');

  const { data } = useQuery({
    queryKey: ['insumos-forn', busca],
    queryFn: () => api.get('/insumos/fornecedores', { params: { busca: busca || undefined } }).then(r => r.data.data),
  });

  const excluir = useMutation({
    mutationFn: (id) => api.delete(`/insumos/fornecedores/${id}`),
    onSuccess: () => qc.invalidateQueries(['insumos-forn']),
  });

  const fornecedores = data || [];

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Building2 size={22} className="text-blue-600" />
          <h1 className="text-xl font-bold text-gray-800">Fornecedores</h1>
        </div>
        <button onClick={() => setModal({})}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
          <Plus size={16} /> Novo Fornecedor
        </button>
      </div>

      <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por nome ou CNPJ..."
        className="w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 max-w-sm" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {fornecedores.map(f => (
          <div key={f.id} className="bg-white rounded-xl border hover:shadow-sm transition-shadow p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-800 truncate">{f.razao_social}</p>
                {f.nome_fantasia && <p className="text-xs text-gray-500 truncate">{f.nome_fantasia}</p>}
              </div>
              <button onClick={() => setModal(f)} className="p-1.5 rounded hover:bg-blue-50 text-blue-500 shrink-0">
                <Edit2 size={14} />
              </button>
            </div>
            <div className="space-y-1.5">
              {f.cnpj && <p className="text-xs text-gray-500 font-mono">{f.cnpj}</p>}
              {f.telefone && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Phone size={11} className="text-gray-400" /> {f.telefone}
                </div>
              )}
              {f.email && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <Mail size={11} className="text-gray-400" /> {f.email}
                </div>
              )}
              {f.contato_nome && (
                <div className="flex items-center gap-1.5 text-xs text-gray-600">
                  <User size={11} className="text-gray-400" /> {f.contato_nome}
                </div>
              )}
            </div>
          </div>
        ))}
        {!fornecedores.length && (
          <div className="col-span-3 text-center py-12 text-gray-400">
            Nenhum fornecedor cadastrado.
            <button onClick={() => setModal({})} className="ml-2 text-blue-600 underline">Cadastre o primeiro.</button>
          </div>
        )}
      </div>

      {modal !== null && <FornecedorModal fornecedor={modal} onClose={() => setModal(null)} />}
    </div>
  );
}
